import React, { useState } from 'react';
import {
    Box,
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Login as LoginIcon,
    Security as SecurityIcon,
} from '@mui/icons-material';

interface FirebaseAdminLoginProps {
    onSignIn: (email: string, password: string) => Promise<{ success: boolean; message: string; requiresMFA?: boolean }>;
    isAuthenticating: boolean;
}

const FirebaseAdminLogin: React.FC<FirebaseAdminLoginProps> = ({ onSignIn, isAuthenticating }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loginAttempts, setLoginAttempts] = useState(0);

    console.log('🎯 FirebaseAdminLogin: Rendering login form');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            const result = await onSignIn(email, password);

            if (result.success) {
                console.log('Login successful:', result.message);
            } else {
                setError(result.message || 'Login failed');
                setLoginAttempts(prev => prev + 1);
            }
        } catch (error: any) {
            setError('An error occurred during login');
            setLoginAttempts(prev => prev + 1);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <Container component="main" maxWidth="sm">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        padding: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        borderRadius: 2,
                        minWidth: 400,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 3,
                        }}
                    >
                        <SecurityIcon sx={{ fontSize: 40, color: 'primary.main', mr: 1 }} />
                        <Typography component="h1" variant="h4" sx={{ fontWeight: 600 }}>
                            Admin Login
                        </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                        Secure Firebase Authentication
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {loginAttempts > 0 && (
                        <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
                            Login attempt {loginAttempts} failed. Please check your credentials.
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={isLoading || isAuthenticating}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={isLoading || isAuthenticating}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleTogglePasswordVisibility}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 3 }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={isLoading || isAuthenticating || !email || !password}
                            startIcon={isLoading || isAuthenticating ? <CircularProgress size={20} /> : <LoginIcon />}
                            sx={{
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 600
                            }}
                        >
                            {isLoading || isAuthenticating ? 'Authenticating...' : 'Login with Firebase'}
                        </Button>
                    </Box>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Need help? Contact your system administrator
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
};

export default FirebaseAdminLogin;
